import { Navigate, Outlet, useLocation } from "react-router";

import { useAuth } from "@/auth/AuthProvider";
import { HOME, STAFF_HOME, signInPath } from "@/auth/next";
import { Button } from "@/ui/Button";
import { Notice } from "@/ui/Notice";
import { Spinner } from "@/ui/primitives";

/**
 * A layout route for everything that needs a signed-in user. Signed out, it sends the
 * user to sign in with the way back in the `next` parameter. A member's pages and the
 * platform console (`staff`) are each other's no-go: whoever is on the wrong side is sent
 * to their own home (ADR-0015).
 */
export function RequireAuth({ staff = false }: { staff?: boolean }) {
  const { state, retry } = useAuth();
  const { pathname, search } = useLocation();

  if (state.status === "signed-out") {
    return <Navigate to={signInPath(pathname + search)} replace />;
  }
  if (state.status === "unreachable") {
    return (
      <main className="grid h-dvh place-items-center bg-bg px-4">
        <div className="w-full max-w-sm space-y-3">
          <Notice tone="danger">
            The server could not be reached. Check your connection, then try again.
          </Notice>
          <Button variant="primary" onClick={retry}>
            Try again
          </Button>
        </div>
      </main>
    );
  }
  if (state.status === "loading") {
    return (
      <main className="grid h-dvh place-items-center bg-bg">
        <Spinner label="Loading" />
      </main>
    );
  }
  if (state.status === "staff" && !staff) {
    return <Navigate to={STAFF_HOME} replace />;
  }
  if (state.status === "signed-in" && staff) {
    return <Navigate to={HOME} replace />;
  }
  return <Outlet />;
}
